import { Navigate, RouteObject, createBrowserRouter } from "react-router-dom";
import * as React from "react";
import ClientDetails from "../../features/admin/clients/details/ClientDetails.tsx";

import RequireAuth from "./RequireAuth.tsx";
import NotFound from "../../features/errors/NotFound.tsx";
import ServerError from "../../features/errors/ServerError.tsx";
import HomePage from "../../features/user/home/HomePage.tsx";
import ManufacturerDashboard from "../../features/admin/manufacturers/dashboard/ManufacturerDashboard.tsx";
import ManufacturerDetails from "../../features/admin/manufacturers/details/ManufacturerDetails.tsx";
import ManufacturerForm from "../../features/admin/manufacturers/form/ManufacturerForm.tsx";
import CategoryDashboard from "../../features/admin/categories/dashboard/CategoryDashboard.tsx";
import CategoryDetails from "../../features/admin/categories/details/CategoryDetails.tsx";
import CategoryForm from "../../features/admin/categories/form/CategoryForm.tsx";
import ProductDetails from "../../features/admin/products/details/ProductDetails.tsx";
import ProductForm from "../../features/admin/products/form/ProductForm.tsx";
import ProductsDashboard from "../../features/admin/products/dashboard/ProductDashboard.tsx";
import AppUserDashboard from "../../features/admin/users/dashboard/AppUserDashboard.tsx";
import AppUserForm from "../../features/admin/users/form/AppUserForm.tsx";
import UserDashboard from "../layout/UserDashboard.tsx";
import UnAuthorized from "../../features/errors/UnAuthorized.tsx";
import LoginForm from "../../features/users/LoginForm.tsx";
import PlaceOrderDashboard from "../../features/user/placeOrder/PlaceOrderDashboard.tsx";
import CartDashboard from "../../features/user/cart/CartDashboard.tsx";
import OrderDashboard from "../../features/admin/orders/dashboard/OrderDashboard.tsx";
import OrderItemDashboard from "../../features/admin/orders/orderItems/OrderItemDashboard.tsx";
import MyOrderDashboard from "../../features/user/myOrders/orders/MyOrderDashboard.tsx";
import MyOrderItemDashboard from "../../features/user/myOrders/orderItems/MyOrderItemDashboard.tsx";
import AddItemsToOrder from "../../features/user/placeOrder/AddItemsToOrder.tsx";
import TaxDashboard from "../../features/admin/taxes/dashboard/TaxDashboard.tsx";
import TaxForm from "../../features/admin/taxes/form/TaxForm.tsx";
import TaxMappingDashboard from "../../features/admin/taxMappings/dashboard/TaxMappingDashboard.tsx";
import TaxMappingForm from "../../features/admin/taxMappings/form/TaxMappingForm.tsx";
import TaxMappingDetails from "../../features/admin/taxMappings/details/TaxMappingDetails.tsx";
import TaxDetails from "../../features/admin/taxes/details/TaxDetails.tsx";
import WarehouseDashboard from "../../features/admin/warehouse/WarehouseDashboard.tsx";
import InventoryProductDetails from "../../features/admin/warehouse/InventoryControl/ProductDetails.tsx";
import ProfileForm from "../../features/profile/ProfileForm.tsx";
import ProfilePage from "../../features/profile/ProfilePage.tsx";
import PrepareShipmentScreen from "../../features/admin/warehouse/OrderHandling/PrepareShipmentScreen.tsx";
import OrderLookupOrderItems from "../../features/admin/warehouse/OrderLookupOrderItems.tsx";
import ViewShipmentScreen from "../../features/admin/warehouse/ShipHandling/ViewShipmentScreen.tsx";
import ReturnManagement from "../../features/admin/warehouse/ReturnHandling/ReturnManagement.tsx";
import CreateReturnScreen from "../../features/admin/warehouse/ReturnHandling/CreateReturn.tsx";
import ViewReturnScreen from "../../features/admin/warehouse/ReturnHandling/ViewReturnScreen.tsx";

// Lazy loaded components
const AdminDashboard = React.lazy(() => import("../layout/AdminDashboard.tsx"));
const ClientDashboard = React.lazy(() => import("../../features/admin/clients/dashboard/ClientDashboard.tsx"));
const App = React.lazy(() => import("../layout/App.tsx"));
const AdminHomePage = React.lazy(() => import("../../features/admin/home/AdminHomePage.tsx"));
const ClientForm = React.lazy(() => import("../../features/admin/clients/form/ClientForm.tsx"));
export const Routes: RouteObject[] = [
  {
    path: "/",
    element: <App />,
    children: [],
  },
  {
    path: "/login",
    element: <LoginForm />,
  },
  {
    path: "/admin",
    element: (
      <RequireAuth role="Admin">
        <AdminDashboard />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <AdminHomePage /> },
      { 
        path: "home", 
        children: [
          { index: true, element: <AdminHomePage /> },
          { path: "dashboard-orders", element: <AdminHomePage /> },
          { path: "dashboard-inventory", element: <AdminHomePage /> },
          { path: "dashboard-sales", element: <AdminHomePage /> },
        ]
      },
      { path: "addorderitem", element: <AddItemsToOrder /> },

      {
        path: "clients",
        children: [
          { path: "", element: <ClientDashboard /> },
          { path: ":id", element: <ClientDetails /> },
          { path: "create", element: <ClientForm /> },
          { path: "edit/:id", element: <ClientForm /> },
        ],
      },
      {
        path: "manufacturers",
        children: [
          { path: "", element: <ManufacturerDashboard /> },
          { path: ":id", element: <ManufacturerDetails /> },
          { path: "create", element: <ManufacturerForm /> },
          { path: "edit/:id", element: <ManufacturerForm /> },
        ],
      },
      {
        path: "categories",
        children: [
          { path: "", element: <CategoryDashboard /> },
          { path: ":id", element: <CategoryDetails /> },
          { path: "create", element: <CategoryForm /> },
          { path: "edit/:id", element: <CategoryForm /> },
        ],
      },
      {
        path: "products",
        children: [
          { path: "", element: <ProductsDashboard /> },
          { path: ":id", element: <ProductDetails /> },
          { path: "create", element: <ProductForm /> },
          { path: "edit/:id", element: <ProductForm /> },
        ],
      },
      {
        path: "users",
        children: [
          { path: "", element: <AppUserDashboard /> },
          { path: "create", element: <AppUserForm /> },
          { path: "edit/:username", element: <AppUserForm /> },
        ],
      },
      {
        path: "user",
        children: [{ path: ":username", element: <ProfileForm /> }],
      },
      {
        path: "orders",
        children: [
          { path: "", element: <OrderDashboard /> },
          { path: ":id", element: <OrderItemDashboard /> },
        ],
      },
      {
        path: "taxes",
        children: [
          { path: "", element: <TaxDashboard /> },
          { path: ":id", element: <TaxDetails /> },
          { path: "create", element: <TaxForm /> },
          { path: "edit/:id", element: <TaxForm /> },
        ],
      },
      {
        path: "tax-mappings",
        children: [
          { path: "", element: <TaxMappingDashboard /> },
          { path: ":id", element: <TaxMappingDetails /> },
          { path: "create", element: <TaxMappingForm /> },
          { path: "edit/:id", element: <TaxMappingForm /> },
        ],
      },
      {
        path: "warehouse",
        children: [
          { path: "", element: <WarehouseDashboard /> },
          { path: ":tab/*", element: <WarehouseDashboard /> },
          {
            path: "inventory-control/edit/:id",
            element: <InventoryProductDetails />,
          },
          {
            path: "inventory-control/create",
            element: <InventoryProductDetails />,
          },
          { path: "prepare-shipment/:id", element: <PrepareShipmentScreen /> },
          {
            path: "order-lookup/:orderId",
            element: <OrderLookupOrderItems />,
          },
          { 
            path: "view-shipment/:id", 
            element: <ViewShipmentScreen /> 
          },
          {
            path: 'returns-management/:id',
            element: <ReturnManagement />
          },
          {
            path: "create-return/:id",
            element: <CreateReturnScreen />
          },
          {
            path: "view-return/:id",
            element: <ViewReturnScreen />
          },
        ],
      },
      { path: "profile", element: <ProfilePage /> },
    ],
  },
  {
    path: "/user",
    element: (
      <RequireAuth role="User">
        <UserDashboard />
      </RequireAuth>
    ),
    children: [
      { path: "", element: <HomePage /> },
      { path: ":username", element: <ProfileForm /> },
      { path: "home", element: <HomePage /> },
      { path: "placeorder", element: <PlaceOrderDashboard /> },
      { path: "cart", element: <CartDashboard /> },
      { path: "addorderitem", element: <AddItemsToOrder /> },
      {
        path: "myorders",
        children: [
          { path: "", element: <MyOrderDashboard /> },
          { path: ":id", element: <MyOrderItemDashboard /> },
        ],
      },
      { path: "profile", element: <ProfilePage /> },
    ],
  },

  { path: "/not-found", element: <NotFound /> },
  { path: "/un-authorized", element: <UnAuthorized /> },
  { path: "/server-error", element: <ServerError /> },
  { path: "*", element: <Navigate replace to="/not-found" /> },
];

export const Router = createBrowserRouter(Routes);
